import { Alert, Button, Empty, Modal, Radio, Result, Select, Spin, Tag } from "@arco-design/web-react";
import { GitBranch, Plus, RefreshCw, Trash2 } from "lucide-react";
import { useState } from "react";

import { useCrmOpportunitySplitRuntime } from "../runtime/useCrmOpportunitySplitRuntime";
import type { OpportunitySplitProject } from "../types";
import "./CrmOpportunitySplitPage.css";

/** 商机拆分工作台（抽屉内嵌）。 */
export function CrmOpportunitySplitFeaturePage({
  opportunityId,
  onClose,
  onSubmitted,
}: {
  opportunityId: number;
  onClose: () => void;
  onSubmitted?: () => void;
}) {
  const [confirmVisible, setConfirmVisible] = useState(false);
  const runtime = useCrmOpportunitySplitRuntime(opportunityId);
  const preview = runtime.preview;

  if (runtime.loading) {
    return (
      <div className="crm-opportunity-split-page is-embedded crm-opportunity-split-page--center">
        <Spin size={32} tip="正在加载拆分预览…" />
      </div>
    );
  }
  if (runtime.error || !preview) {
    return (
      <div className="crm-opportunity-split-page is-embedded crm-opportunity-split-page--center">
        <Result
          status="error"
          title="拆分预览加载失败"
          subTitle={runtime.error instanceof Error ? runtime.error.message : "请刷新后重试"}
          extra={
            <Button icon={<RefreshCw size={16} />} onClick={() => void runtime.refresh()}>
              刷新
            </Button>
          }
        />
      </div>
    );
  }

  const groupOptions = runtime.groupKeys.map((groupKey, index) => ({
    value: groupKey,
    label: `结果组 ${index + 1}`,
  }));
  const groupLabel = new Map(groupOptions.map((item) => [item.value, item.label]));
  const previewGroupByKey = new Map(preview.groups.map((item) => [item.groupKey, item]));
  const conclusionMessages = Array.from(
    new Set(preview.comparisons.map((item) => item.explanation).filter(Boolean)),
  );

  return (
    <div className="crm-opportunity-split-page is-embedded" aria-label="商机拆分内容">
      <div className="crm-opportunity-split-scroll">
        <header className="crm-opportunity-split-header">
          <div>
            <strong className="crm-opportunity-split-header-code">{preview.opportunityCode}</strong>
            <Button
              size="mini"
              icon={<RefreshCw size={14} />}
              loading={runtime.refreshing}
              onClick={() => void runtime.refresh()}
            >
              刷新
            </Button>
          </div>
        </header>

        {preview.blockingConflicts.length ? (
          <Alert type="error" content={preview.blockingConflicts.join("；")} />
        ) : null}

        <section className="crm-opportunity-split-summary" aria-label="源商机摘要">
          <div className="crm-opportunity-split-summary-main">
            <strong>{preview.sourceOpportunity.opportunityName}</strong>
            <div className="crm-opportunity-split-summary-meta">
              <span>{preview.sourceOpportunity.opportunityCode}</span>
              <span>{preview.sourceOpportunity.orgName}</span>
              <span>{preview.sourceOpportunity.ownerUserName ?? "负责人未设置"}</span>
            </div>
          </div>
          <dl>
            <div>
              <dt>定制项目</dt>
              <dd>{preview.sourceOpportunity.projectCount}</dd>
            </div>
            <div>
              <dt>有效订单</dt>
              <dd>{preview.sourceOpportunity.orderCount}</dd>
            </div>
          </dl>
        </section>

        {conclusionMessages.length ? (
          <section className="crm-opportunity-split-conclusions" aria-label="系统结论">
            <Alert
              type="info"
              content={
                <ul className="crm-opportunity-split-conclusion-list">
                  {conclusionMessages.map((explanation) => (
                    <li key={explanation}>{explanation}</li>
                  ))}
                </ul>
              }
            />
          </section>
        ) : null}

        <section className="crm-opportunity-split-groups" aria-label="拆分结果组">
          <div className="crm-opportunity-split-group-toolbar">
            <div className="crm-opportunity-split-group-toolbar-main">
              <span className="crm-opportunity-split-group-toolbar-label">原编号保留给</span>
              <Radio.Group
                type="button"
                value={runtime.originalGroupKey}
                onChange={runtime.setOriginalGroupKey}
              >
                {groupOptions.map((option) => (
                  <Radio key={option.value} value={option.value}>
                    {option.label}
                  </Radio>
                ))}
              </Radio.Group>
            </div>
            <Button icon={<Plus size={16} />} onClick={runtime.addGroup}>
              新增结果组
            </Button>
          </div>

          <div className="crm-opportunity-split-group-grid">
            {runtime.groups.map((group) => {
              const previewGroup = previewGroupByKey.get(group.groupKey);
              const derived = previewGroup?.derivedFields;
              const derivedSummary = derived?.travelMonth
                ? `${derived.travelMonth} · ${derived.travelerCount ?? "未知"}${
                    derived.travelerCount === undefined ? "" : " 人"
                  }`
                : null;
              const showHint =
                Boolean(previewGroup?.hint) &&
                isSameProjectGroup(group.projectIds, previewGroup?.projectIds ?? []);

              return (
                <section key={group.groupKey} className="crm-opportunity-split-group">
                  <header>
                    <div>
                      <GitBranch size={16} />
                      <strong>{groupLabel.get(group.groupKey)}</strong>
                      <Tag size="small">{group.projectIds.length} 个项目</Tag>
                      {derivedSummary ? (
                        <span className="crm-opportunity-split-derived-summary">{derivedSummary}</span>
                      ) : null}
                    </div>
                    {group.projectIds.length === 0 ? (
                      <Button
                        shape="circle"
                        size="mini"
                        status="danger"
                        type="outline"
                        icon={<Trash2 size={14} />}
                        aria-label={`删除${groupLabel.get(group.groupKey)}`}
                        onClick={() => runtime.removeGroup(group.groupKey)}
                      />
                    ) : null}
                  </header>
                  {showHint ? <Alert type="info" content={previewGroup?.hint} /> : null}
                  {group.projectIds.length ? (
                    <div className="crm-opportunity-split-project-list">
                      {group.projectIds.map((projectId) => {
                        const project = preview.projects.find((item) => item.projectId === projectId);
                        return project ? (
                          <ProjectRow
                            key={project.projectId}
                            project={project}
                            groupKey={group.groupKey}
                            groupOptions={groupOptions}
                            onMove={runtime.moveProject}
                          />
                        ) : null;
                      })}
                    </div>
                  ) : (
                    <Empty description="暂无项目" />
                  )}
                </section>
              );
            })}
          </div>
        </section>
      </div>

      <footer className="crm-opportunity-split-footer">
        <p className="crm-opportunity-split-footer-hint">
          定制归属按结果组调整；订单、合同、名单、团号与履约内容不变。
        </p>
        <div className="crm-opportunity-split-footer-actions">
          <Button onClick={onClose}>取消</Button>
          <Button
            type="primary"
            icon={<GitBranch size={16} />}
            loading={runtime.submitting}
            disabled={runtime.groups.filter((group) => group.projectIds.length > 0).length < 2}
            onClick={() => setConfirmVisible(true)}
          >
            确认拆分
          </Button>
        </div>
      </footer>
      <Modal
        title="确定拆分当前商机？"
        visible={confirmVisible}
        okText="确定拆分"
        cancelText="取消"
        confirmLoading={runtime.submitting}
        onCancel={() => setConfirmVisible(false)}
        onOk={async () => {
          await runtime.submit();
          setConfirmVisible(false);
          onSubmitted?.();
          onClose();
        }}
      >
        定制项目归属将按结果组调整；订单、合同、名单、团号和履约业务内容保持不变。
      </Modal>
    </div>
  );
}

/** 判断当前结果组是否仍保持后端推荐的项目集合。 */
function isSameProjectGroup(current: string[], recommended: string[]) {
  return current.length === recommended.length && current.every((item) => recommended.includes(item));
}

/** 展示一个不可拆散的定制项目及其结果组选择。 */
function ProjectRow({
  project,
  groupKey,
  groupOptions,
  onMove,
}: {
  project: OpportunitySplitProject;
  groupKey: string;
  groupOptions: Array<{ value: string; label: string }>;
  onMove: (projectId: string, groupKey: string) => void;
}) {
  const majors = project.majorNames.join("、") || "-";
  const destinations =
    [...project.destinationProvinces, ...project.stayAreaNames].join("、") || "未知";
  const monthPeople = `${project.travelMonth ?? "未知"} / ${project.peopleCount ?? "未知"}${
    project.peopleCount === undefined ? "" : " 人"
  }`;

  return (
    <article className="crm-opportunity-split-project">
      <div className="crm-opportunity-split-project-head">
        <strong className="crm-opportunity-split-project-title">{project.projectName}</strong>
        <div className="crm-opportunity-split-project-code-row">
          <span className="crm-opportunity-split-project-code">{project.projectCode}</span>
          <div className="crm-opportunity-split-project-tags">
            {project.selected ? (
              <Tag size="small" color="arcoblue">
                已采用
              </Tag>
            ) : null}
            {project.hasEffectiveOrder ? (
              <Tag size="small" color="green">
                有订单
              </Tag>
            ) : (
              <Tag size="small">无订单</Tag>
            )}
          </div>
        </div>
      </div>

      <dl className="crm-opportunity-split-project-meta">
        <div>
          <dt>联系人</dt>
          <dd>{project.contactId != null ? "已关联" : "未知"}</dd>
        </div>
        <div>
          <dt>月份 / 人数</dt>
          <dd>{monthPeople}</dd>
        </div>
        <div className="is-wide">
          <dt>专业</dt>
          <dd title={majors}>{majors}</dd>
        </div>
        <div className="is-wide">
          <dt>目的地</dt>
          <dd title={destinations}>{destinations}</dd>
        </div>
        {project.orders.map((order) => (
          <div key={order.orderId} className="is-wide">
            <dt>订单</dt>
            <dd>{`${order.teamId ?? order.orderId} · ${order.travelerCount ?? "未知"}${
              order.travelerCount === undefined ? "" : " 人"
            }`}</dd>
          </div>
        ))}
      </dl>

      <div className="crm-opportunity-split-project-move">
        <span id={`crm-opportunity-split-move-label-${project.projectId}`}>移至结果组</span>
        <Select
          size="small"
          aria-labelledby={`crm-opportunity-split-move-label-${project.projectId}`}
          value={groupKey}
          options={groupOptions}
          getPopupContainer={() => document.body}
          triggerProps={{
            autoFitPosition: true,
            updateOnScroll: true,
            position: "bl",
            popupStyle: { zIndex: 4000 },
          }}
          dropdownMenuClassName="crm-opportunity-split-project-move-dropdown"
          onChange={(value) => onMove(project.projectId, String(value))}
        />
      </div>
    </article>
  );
}
